/**
 * Season state — the single source of truth for "which season is the app
 * currently showing".
 *
 * The whole app filters data by a date window held in two localStorage keys
 * (`currentPlayingseasonStartDate` / `currentPlayingseasonEndDate`) that ~19
 * components read directly. Rather than rewire all of them, this module keeps
 * those keys as the *effective* window and simply swaps them when an admin /
 * coach picks a past season — every existing view then reloads against the
 * new window with no per-component changes.
 *
 * Keys:
 *   currentPlayingseasonStartDate / ...EndDate  — EFFECTIVE window (read by app)
 *   liveSeasonStartDate / liveSeasonEndDate      — stashed live current-season
 *                                                  dates, so "Current" can be
 *                                                  restored after browsing
 *   selectedSeasonYear                           — present iff viewing a past
 *                                                  season (e.g. "2023-24")
 */

import SeasonPhases from "@/services/season-phases-service";
import { Teams } from "@/services/teams-service";

const LS_START = "currentPlayingseasonStartDate";
const LS_END = "currentPlayingseasonEndDate";
const LS_LIVE_START = "liveSeasonStartDate";
const LS_LIVE_END = "liveSeasonEndDate";
const LS_SELECTED = "selectedSeasonYear";

const STAFF_ROLES = ["Admin", "Coach", "HeadCoach"];

/**
 * A canonical season year is "YYYY-YY" where the second part is (year+1)%100.
 * "2026-27" is valid; "2026-07" (a typo for 2026-27) and other digit-shaped
 * junk from bad season_phases rows are not — drop them from the picker.
 */
function isValidSeasonYear(sy) {
  const m = /^(\d{4})-(\d{2})$/.exec(sy || "");
  if (!m) return false;
  return Number(m[2]) === (Number(m[1]) + 1) % 100;
}

/**
 * Season-year string ("2025-26") for a window, derived from its START date.
 * Aug 2025 -> "2025-26". Mirrors the backend getSeasonYear() (June rollover).
 */
function seasonYearFromStart(startDateStr) {
  if (!startDateStr) return null;
  const d = new Date(startDateStr);
  if (isNaN(d)) return null;
  const y = d.getFullYear();
  const base = d.getMonth() + 1 >= 6 ? y : y - 1;
  return `${base}-${String(base + 1).slice(2)}`;
}

/**
 * The backend flags `is_current` from today's calendar date, but a club's
 * team season dates often lag (not rolled over yet) — so the live window may
 * actually be last season's. Re-anchor "current" to the season the live
 * window genuinely belongs to, and align that entry's dates to the live
 * window so the dropdown label and the applied window always correspond.
 */
function reanchorCurrent(seasons, liveStart, liveEnd) {
  const currentYear = seasonYearFromStart(liveStart);
  if (!currentYear) return seasons;

  let found = false;
  const remapped = seasons.map((s) => {
    if (s.season_year === currentYear) {
      found = true;
      return {
        ...s,
        is_current: true,
        start_date: liveStart,
        end_date: liveEnd || s.end_date,
      };
    }
    return { ...s, is_current: false };
  });

  if (!found) {
    remapped.push({
      season_year: currentYear,
      label: currentYear,
      start_date: liveStart,
      end_date: liveEnd,
      is_current: true,
      source: "live",
    });
  }

  remapped.sort((a, b) => (a.season_year < b.season_year ? 1 : -1));
  return remapped;
}

const state = {
  availableSeasons: [],
  // null = viewing the live/current season; otherwise a "2023-24" string
  selectedSeasonYear: localStorage.getItem(LS_SELECTED) || null,
};

const getters = {
  availableSeasons: (s) => s.availableSeasons,
  selectedSeasonYear: (s) => s.selectedSeasonYear,
  selectedSeason: (s) =>
    s.availableSeasons.find((x) => x.season_year === s.selectedSeasonYear) ||
    s.availableSeasons.find((x) => x.is_current) ||
    null,
  isViewingPastSeason: (s) => !!s.selectedSeasonYear,
  // Convenience alias used by the read-only mixin / guardrails
  isReadOnlySeason: (s) => !!s.selectedSeasonYear,
  canSelectSeason: (s, g, rootState, rootGetters) =>
    STAFF_ROLES.includes(rootGetters.getProfile?.userType),
};

const mutations = {
  SET_AVAILABLE_SEASONS(s, seasons) {
    s.availableSeasons = Array.isArray(seasons) ? seasons : [];
  },
  SET_SELECTED_SEASON_YEAR(s, year) {
    s.selectedSeasonYear = year;
  },
};

const actions = {
  /**
   * Load the list of seasons for the current club into the picker.
   * Also stashes the live current-season dates the first time we run while
   * not in a past-season override, so "Current" can be restored later.
   */
  async fetchSeasons({ commit, rootGetters, state }) {
    // Scope the season list to the logged-in user's club, not the build's
    // baked tenant clubId. These are identical in production (one tenant =
    // one club), but the profile clubId is the correct source: it's how the
    // rest of the app scopes user data, and it keeps local/shared-DB testing
    // working when the demo data's clubId differs from the tenant config's.
    const clubId = rootGetters.getProfile?.clubId || rootGetters.clubId;
    if (!clubId) return;

    // Capture the live window before any override can clobber it.
    if (!state.selectedSeasonYear) {
      const start = localStorage.getItem(LS_START);
      const end = localStorage.getItem(LS_END);
      if (start) localStorage.setItem(LS_LIVE_START, start);
      if (end) localStorage.setItem(LS_LIVE_END, end);
    }

    // The selector can mount before any view has set the season window, so
    // the stashed live dates may be missing. Fetch them directly (same call
    // the dashboard uses) so "current" can be re-anchored reliably.
    if (!localStorage.getItem(LS_LIVE_START)) {
      try {
        const live = await Teams.find({
          action: "getCurrentPlayingSeason",
          // Shared multi-tenant DB — scope to this club or we get the wrong one.
          clubId,
        });
        if (live?.data?.season_start_date) {
          localStorage.setItem(LS_LIVE_START, live.data.season_start_date);
          localStorage.setItem(LS_LIVE_END, live.data.season_end_date);
        }
      } catch (e) {
        // Non-fatal: re-anchoring just falls back to the backend's flag.
        console.error("fetchSeasons: live window fetch failed", e);
      }
    }

    try {
      const res = await SeasonPhases.listSeasons(clubId);
      if (res?.data?.success) {
        // Drop semantically-invalid season rows (e.g. typo'd "2026-07").
        const valid = (res.data.data || []).filter((s) =>
          isValidSeasonYear(s.season_year),
        );
        // Re-anchor "current" to the live team window (which may lag the
        // calendar) so the dropdown label matches the window it applies.
        const liveStart = localStorage.getItem(LS_LIVE_START);
        const liveEnd = localStorage.getItem(LS_LIVE_END);
        const seasons = reanchorCurrent(valid, liveStart, liveEnd);
        commit("SET_AVAILABLE_SEASONS", seasons);
      }
    } catch (e) {
      // Non-fatal: picker just won't populate.
      console.error("fetchSeasons failed", e);
    }
  },

  /**
   * Switch the whole app to a season. Writes the effective date window then
   * reloads so every view re-fetches against it. Passing the current season
   * (or null) restores the live window.
   */
  selectSeason({ commit, state }, seasonYear) {
    const season = state.availableSeasons.find(
      (x) => x.season_year === seasonYear,
    );

    if (!season || season.is_current) {
      // Back to live: restore stashed current-season dates, drop override.
      const liveStart = localStorage.getItem(LS_LIVE_START);
      const liveEnd = localStorage.getItem(LS_LIVE_END);
      if (liveStart) localStorage.setItem(LS_START, liveStart);
      if (liveEnd) localStorage.setItem(LS_END, liveEnd);
      localStorage.removeItem(LS_SELECTED);
      commit("SET_SELECTED_SEASON_YEAR", null);
    } else {
      localStorage.setItem(LS_START, season.start_date);
      localStorage.setItem(LS_END, season.end_date);
      localStorage.setItem(LS_SELECTED, season.season_year);
      commit("SET_SELECTED_SEASON_YEAR", season.season_year);
    }

    // Hard reload guarantees all ~19 consumers re-read the new window.
    window.location.reload();
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
